
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {DepositMarginResponse} from './DepositMarginResponse';
import {AccountUpdate} from './AccountUpdate';
import {FieldModelAccountUpdate} from './AccountUpdate';
import {AccountMarginUpdate} from './AccountMarginUpdate';
import {FieldModelAccountMarginUpdate} from './AccountMarginUpdate';
import {TradeHistoryRecord} from '../core/TradeHistoryRecord';
import {FieldModelTradeHistoryRecord} from '../core/TradeHistoryRecord';

/**
 * DepositMarginRequest struct
 */
class DepositMarginRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!AccountUpdate=} account
   * @param {!AccountMarginUpdate=} margin
   * @param {!TradeHistoryRecord=} depositTradeHistoryRecord
   * @constructor
   */
  constructor (parent = new Request(), account = new AccountUpdate(), margin = new AccountMarginUpdate(), depositTradeHistoryRecord = new TradeHistoryRecord()) {
    super()
    super.copy(parent)
    this.Account = account
    this.Margin = margin
    this.DepositTradeHistoryRecord = depositTradeHistoryRecord
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DepositMarginRequest}
   * @param {!DepositMarginRequest} other Other struct
   * @returns {!DepositMarginRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Account != null) {
      this.Account = AccountUpdate.fromObject(other.Account)
    } else {
      this.Account = null
    }
    if (other.Margin != null) {
      this.Margin = AccountMarginUpdate.fromObject(other.Margin)
    } else {
      this.Margin = null
    }
    if (other.DepositTradeHistoryRecord != null) {
      this.DepositTradeHistoryRecord = TradeHistoryRecord.fromObject(other.DepositTradeHistoryRecord)
    } else {
      this.DepositTradeHistoryRecord = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DepositMarginRequest}
   * @returns {!DepositMarginRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DepositMarginRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DepositMarginRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DepositMarginRequest}
   * @param {!DepositMarginRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DepositMarginRequest)) {
      throw new TypeError('Instance of DepositMarginRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DepositMarginRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Account: ((this.Account != null) ? this.Account : null),
      Margin: ((this.Margin != null) ? this.Margin : null),
      DepositTradeHistoryRecord: ((this.DepositTradeHistoryRecord != null) ? this.DepositTradeHistoryRecord : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DepositMarginRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DepositMarginRequest} other Object value
   * @returns {!DepositMarginRequest} Created struct
   */
  static fromObject (other) {
    return new DepositMarginRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DepositMarginRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DepositMarginRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DepositMarginRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 4093
  }
}

export { DepositMarginRequest };

/**
 * Fast Binary Encoding DepositMarginRequest field model
 */
class FieldModelDepositMarginRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.Account = new FieldModelAccountUpdate(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Margin = new FieldModelAccountMarginUpdate(buffer, this.Account.fbeOffset + this.Account.fbeSize)
    this.DepositTradeHistoryRecord = new FieldModelTradeHistoryRecord(buffer, this.Margin.fbeOffset + this.Margin.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelDepositMarginRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelDepositMarginRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDepositMarginRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Account.fbeSize + this.Margin.fbeSize + this.DepositTradeHistoryRecord.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDepositMarginRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Account.fbeExtra + this.Margin.fbeExtra + this.DepositTradeHistoryRecord.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDepositMarginRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDepositMarginRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDepositMarginRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 4093
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDepositMarginRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDepositMarginRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Account.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Account.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Account.fbeSize

    if ((fbeCurrentSize + this.Margin.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Margin.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margin.fbeSize

    if ((fbeCurrentSize + this.DepositTradeHistoryRecord.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DepositTradeHistoryRecord.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DepositTradeHistoryRecord.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDepositMarginRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDepositMarginRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDepositMarginRequest}
   * @param {!DepositMarginRequest} fbeValue Default value, defaults is new DepositMarginRequest()
   * @returns {!DepositMarginRequest} DepositMarginRequest value
   */
  get (fbeValue = new DepositMarginRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDepositMarginRequest}
   * @param {!DepositMarginRequest} fbeValue DepositMarginRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Account.fbeSize) <= fbeStructSize) {
      fbeValue.Account = this.Account.get()
    } else {
      fbeValue.Account = new AccountUpdate()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Account.fbeSize

    if ((fbeCurrentSize + this.Margin.fbeSize) <= fbeStructSize) {
      fbeValue.Margin = this.Margin.get()
    } else {
      fbeValue.Margin = new AccountMarginUpdate()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Margin.fbeSize

    if ((fbeCurrentSize + this.DepositTradeHistoryRecord.fbeSize) <= fbeStructSize) {
      fbeValue.DepositTradeHistoryRecord = this.DepositTradeHistoryRecord.get()
    } else {
      fbeValue.DepositTradeHistoryRecord = new TradeHistoryRecord()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DepositTradeHistoryRecord.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDepositMarginRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDepositMarginRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDepositMarginRequest}
   * @param {!DepositMarginRequest} fbeValue DepositMarginRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDepositMarginRequest}
   * @param {DepositMarginRequest} fbeValue DepositMarginRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Account.set(fbeValue.Account)
    this.Margin.set(fbeValue.Margin)
    this.DepositTradeHistoryRecord.set(fbeValue.DepositTradeHistoryRecord)
  }
}

export { FieldModelDepositMarginRequest };

/**
 * Fast Binary Encoding DepositMarginRequest model
 */
class DepositMarginRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDepositMarginRequest(this.buffer, 4)
  }

  /**
   * Get the DepositMarginRequest model
   * @this {!DepositMarginRequestModel}
   * @returns {!FieldModelDepositMarginRequest} model DepositMarginRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DepositMarginRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DepositMarginRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DepositMarginRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DepositMarginRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDepositMarginRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DepositMarginRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DepositMarginRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DepositMarginRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DepositMarginRequestModel}
   * @param {!DepositMarginRequest} value DepositMarginRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DepositMarginRequestModel}
   * @param {!DepositMarginRequest} value DepositMarginRequest value, defaults is new DepositMarginRequest()
   * @return {!object} Deserialized DepositMarginRequest value and its size
   */
  deserialize (value = new DepositMarginRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DepositMarginRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DepositMarginRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DepositMarginRequestModel}
   * @param {!number} prev Previous DepositMarginRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DepositMarginRequestModel };
DepositMarginRequest.__has_response = true;
DepositMarginRequest.__response_class = DepositMarginResponse;
